import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';

/**
 * Convert a YYYY-MM-DD day string to the UTC ISO timestamp at local 00:00:00.000
 * on that day. Used to translate filter-bar date inputs into UTC instants the
 * server can compare directly against TIMESTAMPTZ columns — no server-side
 * timezone logic required.
 */
export function dayStartToUtcIso(day: string): string {
  return boundaryToUtcIso(day, 0, 0, 0, 0);
}

/**
 * Convert a YYYY-MM-DD day string to the UTC ISO timestamp at local 23:59:59.999
 * on that day (inclusive end-of-day boundary).
 */
export function dayEndToUtcIso(day: string): string {
  return boundaryToUtcIso(day, 23, 59, 59, 999);
}

function boundaryToUtcIso(day: string, h: number, m: number, s: number, ms: number): string {
  const [y, mo, d] = day.split('-').map(Number);
  if (!y || !mo || !d) throw new Error(`invalid day string: ${day}`);
  return new Date(y, mo - 1, d, h, m, s, ms).toISOString();
}

/**
 * Translate a filter-bar date range (day strings in local tz) into the
 * dateFrom/dateTo query params the API expects (UTC ISO instants).
 */
export function toUtcDateRange(input: { from?: string | null; to?: string | null }): {
  dateFrom?: string;
  dateTo?: string;
} {
  const out: { dateFrom?: string; dateTo?: string } = {};
  if (input.from) out.dateFrom = dayStartToUtcIso(input.from);
  if (input.to) out.dateTo = dayEndToUtcIso(input.to);
  return out;
}

/**
 * What a date-range SELECTION means, independent of the control that made it.
 *
 * A selection is a named period ("last quarter") plus the two explicit bounds
 * a Custom pick uses — and the two are not interchangeable. A named period is
 * a rule that re-answers itself: a filter left on "this month" is about
 * whatever month it is read in, which is the whole reason somebody picks a
 * period instead of typing two dates. So the PERIOD is what gets stored, and
 * the window is COMPUTED from it every time the selection is used.
 *
 * Adapted from the equivalent model in the Clockwise codebase. That one does
 * its own `YYYY-MM-DD` string arithmetic because it lives in a package shared
 * with an API that must not depend on a date library; here the model is
 * web-only and `date-fns` is already a dependency of this app, so the
 * calendar math is delegated rather than re-implemented.
 */

export const DATE_RANGE_PRESETS = [
  'all_time',
  'current_week',
  'previous_week',
  'current_month',
  'previous_month',
  'current_quarter',
  'previous_quarter',
  'current_year',
  'previous_year',
  'custom',
] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number];

/**
 * THIS AND LAST, not CURRENT and PREVIOUS — the shorter pair is the one
 * people say out loud, and these are read on a filter bar where width is
 * scarce. One vocabulary, so a period picked by name reads back by the same
 * name wherever it is shown.
 */
export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  all_time: 'All time',
  current_week: 'This week',
  previous_week: 'Last week',
  current_month: 'This month',
  previous_month: 'Last month',
  current_quarter: 'This quarter',
  previous_quarter: 'Last quarter',
  current_year: 'This year',
  previous_year: 'Last year',
  custom: 'Custom range',
};

/** A selection: a period, plus `from`/`to` used only when it is `custom`. */
export interface DateRangeValue {
  preset: DateRangePreset;
  /** `yyyy-MM-dd`, custom only. */
  from?: string;
  /** `yyyy-MM-dd`, custom only. */
  to?: string;
}

/** A concrete, inclusive `yyyy-MM-dd` window. */
export interface ResolvedDateRange {
  from: string;
  to: string;
}

const ISO = 'yyyy-MM-dd';

/**
 * Monday, matching the only other week the app draws
 * (`OfferedSlotsDialog`). date-fns defaults to Sunday, so every week call
 * here passes this explicitly — a "this week" that disagreed with the
 * scheduling grid by a day would be two weeks with one name.
 */
const WEEK = { weekStartsOn: 1 } as const;

export function toIsoDate(date: Date): string {
  return format(date, ISO);
}

/** Parse a `yyyy-MM-dd` bound, or `undefined` if it is absent or nonsense. */
export function fromIsoDate(iso: string | undefined): Date | undefined {
  if (!iso) return undefined;
  const parsed = parse(iso, ISO, new Date());
  return isValid(parsed) ? parsed : undefined;
}

function span(from: Date, to: Date): ResolvedDateRange {
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function presetRange(preset: DateRangePreset, today: Date): ResolvedDateRange | null {
  switch (preset) {
    case 'current_week':
      return span(startOfWeek(today, WEEK), endOfWeek(today, WEEK));
    case 'previous_week': {
      const week = subWeeks(today, 1);
      return span(startOfWeek(week, WEEK), endOfWeek(week, WEEK));
    }
    case 'current_month':
      return span(startOfMonth(today), endOfMonth(today));
    case 'previous_month': {
      const month = subMonths(today, 1);
      return span(startOfMonth(month), endOfMonth(month));
    }
    case 'current_quarter':
      return span(startOfQuarter(today), endOfQuarter(today));
    case 'previous_quarter': {
      const quarter = subQuarters(today, 1);
      return span(startOfQuarter(quarter), endOfQuarter(quarter));
    }
    case 'current_year':
      return span(startOfYear(today), endOfYear(today));
    case 'previous_year': {
      const year = subYears(today, 1);
      return span(startOfYear(year), endOfYear(year));
    }
    // `all_time` resolves to no window on purpose — callers read null as
    // "don't bound this query" rather than as an error.
    default:
      return null;
  }
}

/**
 * Resolve a selection to a concrete window, or `null` when it has none.
 *
 * Named periods compute against `today`, so a stored `previous_month` always
 * means the month before whenever it is read. Custom uses its own dates and
 * returns `null` when they are missing or inverted — a half-picked range is
 * not a window, and a caller that treated it as one would query a backwards
 * span and render the empty result as "no calls".
 *
 * `today` is injectable so the arithmetic is testable without mocking the
 * clock.
 */
export function resolveDateRange(
  value: DateRangeValue,
  today: Date = new Date()
): ResolvedDateRange | null {
  if (value.preset !== 'custom') return presetRange(value.preset, today);

  const { from, to } = value;
  if (!from || !to) return null;
  if (!fromIsoDate(from) || !fromIsoDate(to)) return null;
  // Both are `yyyy-MM-dd`, so a lexical compare IS a chronological one.
  if (to < from) return null;
  return { from, to };
}

/** "Jun 1 – Jun 3, 2026" for a resolved window, or `''` when there is none. */
export function prettyDateRange(range: ResolvedDateRange | null): string {
  if (!range) return '';
  const from = fromIsoDate(range.from);
  const to = fromIsoDate(range.to);
  if (!from || !to) return '';
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${format(from, sameYear ? 'MMM d' : 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
}

/**
 * The one-line summary for a trigger button or a chip.
 *
 * A named period reads as its NAME, because that is what was chosen and it
 * stays true as time passes. A Custom pick has no name — two dates somebody
 * typed are not called anything — so it reads as its dates, and falls back to
 * "Custom range" while only one of them has been picked.
 */
export function dateRangeLabel(value: DateRangeValue): string {
  if (value.preset !== 'custom') return DATE_RANGE_PRESET_LABELS[value.preset];
  return prettyDateRange(resolveDateRange(value)) || DATE_RANGE_PRESET_LABELS.custom;
}
